import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, MapPin, Phone, User, Package, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const statusColors = {
  pending: "bg-amber-50 border-amber-200 text-amber-700",
  confirmed: "bg-blue-50 border-blue-200 text-blue-700",
  processing: "bg-violet-50 border-violet-200 text-violet-700",
  shipped: "bg-cyan-50 border-cyan-200 text-cyan-700",
  delivered: "bg-green-50 border-green-200 text-green-700",
  cancelled: "bg-red-50 border-red-200 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { getToken } = useAuth();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers });
      setOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (orderId, field, value) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { [field]: value }, { headers });
      toast.success("Order updated");
      fetchOrders();
    } catch (e) { toast.error("Update failed"); }
  };

  const formatDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Orders ({orders.length})</h1>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg" />)}</div>
      ) : orders.length === 0 ? (
        <Card className="border-gray-200"><CardContent className="p-8 text-center text-gray-400">No orders yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const isOpen = expanded === o.id;
            const addr = o.shipping_address || {};
            const badgeClass = statusColors[o.status] || statusColors.pending;

            return (
              <Card key={o.id} className="border-gray-200 overflow-hidden" data-testid={`order-card-${o.id?.slice(0,8)}`}>
                {/* Header row — always visible */}
                <button
                  className="w-full text-left px-4 py-3 md:px-5 md:py-4 flex items-center gap-3 hover:bg-gray-50/60 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : o.id)}
                >
                  <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-y-1 gap-x-4 items-center">
                    <div>
                      <p className="font-mono text-xs text-gray-400">#{o.id?.slice(0, 8)}</p>
                      <p className="font-medium text-gray-800 text-sm truncate">{addr.full_name || addr.name || "—"}</p>
                    </div>
                    <div className="text-right md:text-left">
                      <p className="font-heading text-lg font-bold text-gray-900">₹{o.total}</p>
                      <p className="text-xs text-gray-400">{o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? "s" : ""}</p>
                    </div>
                    <div className="hidden md:block">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${badgeClass}`}>
                        {o.status}
                      </span>
                    </div>
                    <div className="hidden md:flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" /> {formatDate(o.created_at)}
                    </div>
                  </div>
                  <div className="shrink-0 text-gray-400">
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50/40 px-4 py-4 md:px-5 md:py-5 space-y-5">
                    {/* Status Controls */}
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Order Status</p>
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, "status", v)}>
                          <SelectTrigger className="w-40 h-9 text-sm" data-testid={`status-select-${o.id?.slice(0,8)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Payment</p>
                        <Select value={o.payment_status} onValueChange={(v) => updateStatus(o.id, "payment_status", v)}>
                          <SelectTrigger className="w-36 h-9 text-sm" data-testid={`payment-select-${o.id?.slice(0,8)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="refunded">Refunded</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Order Date</p>
                        <p className="text-sm text-gray-700 mt-1.5">{formatDate(o.created_at)}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      {/* Customer & Address */}
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer & Shipping</p>
                        <div className="bg-white rounded-lg border border-gray-100 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-800">{addr.full_name || addr.name || "—"}</span>
                          </div>
                          {addr.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">{addr.phone}</span>
                            </div>
                          )}
                          {addr.email && (
                            <div className="flex items-center gap-2">
                              <span className="w-4 h-4 text-gray-400 text-center text-xs">@</span>
                              <span className="text-sm text-gray-700">{addr.email}</span>
                            </div>
                          )}
                          <div className="flex items-start gap-2 pt-1 border-t border-gray-50">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                            <div className="text-sm text-gray-700 space-y-0.5">
                              {(addr.address_line1 || addr.address) && <div>{addr.address_line1 || addr.address}</div>}
                              {addr.address_line2 && <div>{addr.address_line2}</div>}
                              <div>{[addr.city, addr.state].filter(Boolean).join(", ")}</div>
                              {addr.pincode && <div>PIN: {addr.pincode}</div>}
                              {addr.country && <div>{addr.country}</div>}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Items Ordered</p>
                        <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-50">
                          {o.items?.map((item, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2.5" data-testid={`order-item-${i}`}>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{item.product_name}</p>
                                <p className="text-xs text-gray-400">Qty: {item.quantity} {item.price ? `× ₹${item.price}` : ""}</p>
                              </div>
                              <span className="text-sm font-semibold text-gray-900 shrink-0 ml-3">
                                ₹{item.item_total?.toFixed(0) || (item.price * item.quantity)?.toFixed(0) || "—"}
                              </span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50/80">
                            <span className="text-sm font-semibold text-gray-600">Total</span>
                            <span className="text-base font-bold text-gray-900">₹{o.total}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order ID */}
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-400">Order ID: <span className="font-mono">{o.id}</span></p>
                      {o.user_id && <p className="text-xs text-gray-400">User ID: <span className="font-mono">{o.user_id}</span></p>}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
